import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Settings, Users, 
  MessageCircle, Heart, Share2, Crown, Eye, Clock, Calendar,
  Wifi, WifiOff, Zap, Star, Trophy, Target, TrendingUp, Lock,
  AlertTriangle, CheckCircle, X, ChevronLeft, ChevronRight, User,
  ArrowRight, ArrowLeft, CalendarDays, MapPin, Award, Flame, Gift
} from 'lucide-react';
import { database } from '../../Config/firebase';
import { ref, onValue, push, set, get, query, orderByChild } from '../../Config/matuRtdb';
import useUserData from '../../Hooks/useUserData';
import { useNavigate } from 'react-router-dom';

const LiveStreaming = () => {
  const { userData, loading: userLoading } = useUserData();
  const navigate = useNavigate();
  
  const [currentStream, setCurrentStream] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [viewers, setViewers] = useState(0);
  const [likes, setLikes] = useState(0);
  const [showChat, setShowChat] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [showPromotion, setShowPromotion] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState(null);
  const [showAd, setShowAd] = useState(false);
  const [adData, setAdData] = useState(null);
  const [streamsData, setStreamsData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [viewMode, setViewMode] = useState('highlights'); // 'highlights', 'live', 'upcoming'
  
  const videoRef = useRef(null);
  const chatRef = useRef(null);
  const containerRef = useRef(null);

  // Detectar dispositivo
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Verificar autenticación
  useEffect(() => {
    if (!userLoading && !userData) {
      navigate('/login');
      return;
    }
  }, [userData, userLoading, navigate]);

  // Cargar datos desde Firebase
  useEffect(() => {
    setIsLoading(true);
    
    // Cargar categorías
    const categoriesRef = ref(database, 'streaming/categories');
    const categoriesUnsubscribe = onValue(categoriesRef, (snapshot) => {
      if (snapshot.exists()) {
        const categoriesData = [];
        snapshot.forEach((childSnapshot) => {
          categoriesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setCategories(categoriesData);
      }
    });

    // Cargar streams
    const streamsRef = ref(database, 'streaming/events');
    const streamsUnsubscribe = onValue(streamsRef, (snapshot) => {
      if (snapshot.exists()) {
        const streamsData = [];
        snapshot.forEach((childSnapshot) => {
          const stream = {
            id: childSnapshot.key,
            ...childSnapshot.val()
          };
          // Mostrar todos los streams (live, upcoming, finished)
          streamsData.push(stream);
        });
        setStreamsData(streamsData);
      }
      setIsLoading(false);
    });

    // Cargar promociones
    const promotionsRef = ref(database, 'streaming/promotions');
    const promotionsUnsubscribe = onValue(promotionsRef, (snapshot) => {
      if (snapshot.exists()) {
        const promotions = [];
        snapshot.forEach((childSnapshot) => {
          const promotion = {
            id: childSnapshot.key,
            ...childSnapshot.val()
          };
          if (promotion.active) {
            promotions.push(promotion);
          }
        });
        
        // Mostrar promoción aleatoria cada 30 segundos
        if (promotions.length > 0) {
          const randomPromotion = promotions[Math.floor(Math.random() * promotions.length)];
          setCurrentPromotion(randomPromotion);
          
          // Mostrar promoción después de 5 segundos
          setTimeout(() => {
            setShowPromotion(true);
          }, 5000);
        }
      }
    });

    return () => {
      categoriesUnsubscribe();
      streamsUnsubscribe();
      promotionsUnsubscribe();
    };
  }, [database]);

  useEffect(() => {
    // Simular actualización de espectadores (mínimo 1,000)
    const interval = setInterval(() => {
      setViewers(prev => {
        const currentViewers = prev || 1000;
        const change = Math.floor(Math.random() * 200) - 100; // Cambio entre -100 y +100
        const newViewers = currentViewers + change;
        return Math.max(1000, newViewers); // Mínimo 1,000 espectadores
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Controles del video personalizado
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * duration;
    
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Mostrar/ocultar controles
  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeout) {
      clearTimeout(controlsTimeout);
    }
    const timeout = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
    setControlsTimeout(timeout);
  };

  const sendMessage = () => {
    if (newMessage.trim() && currentStream && userData) {
      const message = {
        id: Date.now(),
        user: userData.name || 'Usuario',
        message: newMessage,
        timestamp: Date.now(),
        isVIP: userData.typeU === 'Cuenta Vip',
        userId: userData.uid
      };
      
      setChatMessages(prev => [...prev, message]);
      setNewMessage('');
      
      // Guardar en Firebase
      const chatRef = ref(database, `streaming/chat/${currentStream.id}`);
      push(chatRef, message);
    }
  };

  const handleLike = () => {
    setLikes(prev => prev + 1);
    if (currentStream) {
      const likesRef = ref(database, `streaming/events/${currentStream.id}/likes`);
      set(likesRef, likes + 1);
    }
  };

  const startStream = (stream) => {
    // Verificar si es VIP y el stream requiere VIP
    if (stream.isVIP && userData?.typeU !== 'Cuenta Vip') {
      setShowVipModal(true);
      return;
    }
    
    setCurrentStream(stream);
    // Asegurar mínimo 1,000 espectadores
    const initialViewers = Math.max(1000, stream.viewers || 1000);
    setViewers(initialViewers);
    setLikes(stream.likes || 0);
    setIsPlaying(true);
    setShowVipModal(false);
    setShowScoreboard(false); // Ocultar marcador al reproducir
  };

  // Filtrar streams según el modo de vista y categoría
  const getFilteredStreams = () => {
    let filtered = streamsData;
    
    // Filtrar por modo de vista
    if (viewMode === 'highlights') {
      filtered = filtered.filter(stream => stream.status === 'finished');
    } else if (viewMode === 'live') {
      filtered = filtered.filter(stream => stream.status === 'live');
    } else if (viewMode === 'upcoming') {
      filtered = filtered.filter(stream => stream.status === 'upcoming');
    }
    
    // Filtrar por categoría
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(stream => stream.category === selectedCategory);
    }
    
    return filtered;
  };

  const filteredStreams = getFilteredStreams();

  // Cargar mensajes del chat desde Firebase
  useEffect(() => {
    if (currentStream) {
      const chatRef = ref(database, `streaming/chat/${currentStream.id}`);
      const unsubscribe = onValue(chatRef, (snapshot) => {
        if (snapshot.exists()) {
          const messages = [];
          snapshot.forEach((childSnapshot) => {
            messages.push(childSnapshot.val());
          });
          setChatMessages(messages);
        }
      });

      return () => unsubscribe();
    }
  }, [currentStream, database]);

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Banner Principal Deportivo */}
      <div className="relative overflow-hidden bg-gradient-to-r from-red-900/50 via-blue-900/50 to-green-900/50">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative max-w-7xl mx-auto p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="relative">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-ping opacity-75"></div>
              </div>
              <div>
                <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-red-400 to-blue-400 bg-clip-text text-transparent">
                  DeportivosPro Live
                </h1>
                <p className="text-gray-400 text-xs md:text-sm">Partidos en vivo en tiempo real</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-6 flex-wrap">
              <div className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 px-3 py-1 md:px-4 md:py-2 rounded-full shadow-lg">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full animate-pulse"></div>
                <span className="text-xs md:text-sm font-bold">EN VIVO</span>
              </div>
              
              <div className="flex items-center gap-1 md:gap-2 bg-gray-800/50 backdrop-blur-sm px-2 py-1 md:px-4 md:py-2 rounded-full">
                <Users className="w-3 h-3 md:w-4 md:h-4 text-blue-400" />
                <span className="text-xs md:text-sm font-medium">{viewers.toLocaleString()}</span>
              </div>
              
              <div className="flex items-center gap-1 md:gap-2 bg-gray-800/50 backdrop-blur-sm px-2 py-1 md:px-4 md:py-2 rounded-full">
                <Wifi className="w-3 h-3 md:w-4 md:h-4 text-green-400" />
                <span className="text-xs md:text-sm font-medium">Conectado</span>
              </div>

              {/* Mostrar tipo de cuenta */}
              <div className="flex items-center gap-1 md:gap-2 bg-gray-800/50 backdrop-blur-sm px-2 py-1 md:px-4 md:py-2 rounded-full">
                {userData.typeU === 'Cuenta Vip' ? (
                  <>
                    <Crown className="w-3 h-3 md:w-4 md:h-4 text-yellow-400" />
                    <span className="text-xs md:text-sm font-medium text-yellow-400">VIP</span>
                  </>
                ) : (
                  <>
                    <User className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                    <span className="text-xs md:text-sm font-medium text-gray-400">Gratis</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Banner de Eventos Destacados */}
          <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl p-4 mb-6 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-orange-400" />
              <h2 className="text-lg md:text-xl font-bold text-white">Eventos Destacados</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-gradient-to-r from-red-600/20 to-red-800/20 p-3 rounded-lg border border-red-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-semibold text-red-300">La Liga</span>
                </div>
                <p className="text-xs text-gray-300">Real Madrid vs Barcelona</p>
              </div>
              <div className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 p-3 rounded-lg border border-blue-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-semibold text-blue-300">Premier League</span>
                </div>
                <p className="text-xs text-gray-300">Manchester City vs Liverpool</p>
              </div>
              <div className="bg-gradient-to-r from-green-600/20 to-green-800/20 p-3 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-semibold text-green-300">NBA</span>
                </div>
                <p className="text-xs text-gray-300">Lakers vs Warriors</p>
              </div>
            </div>
          </div>

                    {/* Filtros de vista y categorías */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            {/* Modos de vista */}
            <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setViewMode('highlights')}
                className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full transition-all whitespace-nowrap ${
                  viewMode === 'highlights'
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg'
                    : 'bg-gray-800/50 hover:bg-gray-700/50 text-gray-300'
                }`}
              >
                <Play className="w-4 h-4" />
                <span className="font-medium text-sm md:text-base">Resúmenes</span>
              </button>
              <button
                onClick={() => setViewMode('live')}
                className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full transition-all whitespace-nowrap ${
                  viewMode === 'live'
                    ? 'bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-lg'
                    : 'bg-gray-800/50 hover:bg-gray-700/50 text-gray-300'
                }`}
              >
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-sm md:text-base">En Vivo</span>
              </button>
              <button
                onClick={() => setViewMode('upcoming')}
                className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full transition-all whitespace-nowrap ${
                  viewMode === 'upcoming'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-800/50 hover:bg-gray-700/50 text-gray-300'
                }`}
              >
                <Clock className="w-4 h-4" />
                <span className="font-medium text-sm md:text-base">Próximos</span>
              </button>
            </div>

            {/* Filtros de categorías */}
            <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full transition-all whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-800/50 hover:bg-gray-700/50 text-gray-300'
                  }`}
                >
                  <span className="text-base md:text-lg">{category.icon}</span>
                  <span className="font-medium text-sm md:text-base">{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 md:gap-6">
          {/* Video Player Principal */}
          <div className="xl:col-span-3">
            {currentStream ? (
              <div 
                ref={containerRef}
                className="relative bg-black rounded-xl md:rounded-2xl overflow-hidden shadow-2xl"
                onMouseMove={showControlsTemporarily}
                onMouseLeave={() => {
                  if (isPlaying) {
                    setShowControls(false);
                  }
                }}
              >
                {/* Video personalizado */}
                {currentStream.videoUrl.includes('youtube.com') ? (
                  <iframe
                    src={`${currentStream.videoUrl}?autoplay=1&mute=0&controls=1&rel=0&modestbranding=1`}
                    title={currentStream.title}
                    className="w-full aspect-video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <video
                    ref={videoRef}
                    className="w-full aspect-video"
                    poster={currentStream.thumbnail}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleTimeUpdate}
                  >
                    <source src={currentStream.videoUrl} type="video/mp4" />
                    Tu navegador no soporta el elemento video.
                  </video>
                )}

                {/* Badge de calidad */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-bold">
                  {currentStream.quality}
                </div>

                {/* Botón para mostrar marcador */}
                {!showScoreboard && (
                  <button
                    onClick={() => setShowScoreboard(true)}
                    className="absolute top-4 left-4 bg-black/80 backdrop-blur-md rounded-lg p-2 border border-gray-700 hover:bg-black/90 transition-all"
                  >
                    <Trophy className="w-4 h-4 text-yellow-400" />
                  </button>
                )}

                {/* Marcador (solo cuando no está reproduciendo) */}
                {showScoreboard && !isPlaying && (
                  <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-md rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-gray-300 font-medium">{currentStream.league}</span>
                    </div>
                    <div className="flex items-center gap-4 text-white font-bold">
                      <span className="text-lg">{currentStream.homeTeam}</span>
                      <div className="bg-gradient-to-r from-red-600 to-blue-600 px-4 py-2 rounded-lg">
                        <span className="text-2xl font-black">{currentStream.score}</span>
                      </div>
                      <span className="text-lg">{currentStream.awayTeam}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-400 font-bold">{currentStream.time}</span>
                    </div>
                    <button
                      onClick={() => setShowScoreboard(false)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Controles del video personalizados */}
                {showControls && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 md:p-6">
                    {/* Barra de progreso */}
                    <div 
                      className="w-full h-1 bg-gray-600 rounded-full mb-4 cursor-pointer"
                      onClick={handleSeek}
                    >
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 md:gap-6">
                        <button
                          onClick={handlePlayPause}
                          className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-2 md:p-3 rounded-full transition-all"
                        >
                          {isPlaying ? <Pause className="w-5 h-5 md:w-6 md:h-6" /> : <Play className="w-5 h-5 md:w-6 md:h-6" />}
                        </button>
                        
                        <div className="flex items-center gap-2 md:gap-3">
                          <button
                            onClick={toggleMute}
                            className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-1.5 md:p-2 rounded-full transition-all"
                          >
                            {isMuted ? <VolumeX className="w-4 h-4 md:w-5 md:h-5" /> : <Volume2 className="w-4 h-4 md:w-5 md:h-5" />}
                          </button>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            className="w-16 md:w-24 accent-blue-500"
                          />
                        </div>

                        <div className="text-xs md:text-sm text-white hidden md:block">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 md:gap-4">
                        <button
                          onClick={() => setShowChat(!showChat)}
                          className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-1.5 md:p-2 rounded-full transition-all"
                        >
                          <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <button
                          onClick={toggleFullscreen}
                          className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-1.5 md:p-2 rounded-full transition-all"
                        >
                          <Maximize className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Información del stream */}
                <div className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 gap-3">
                    <div>
                      <h2 className="text-lg md:text-2xl font-bold mb-2">{currentStream.title}</h2>
                      <p className="text-gray-300 text-sm md:text-base mb-4">{currentStream.description}</p>
                    </div>
                    {currentStream.isVIP && (
                      <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 px-3 py-1 rounded-full self-start">
                        <Crown className="w-4 h-4" />
                        <span className="text-sm font-bold text-black">VIP</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3 md:gap-6 flex-wrap">
                      <div className="flex items-center gap-2 bg-gray-800/50 px-3 py-2 rounded-lg">
                        <Eye className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium">{viewers.toLocaleString()} espectadores</span>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-800/50 px-3 py-2 rounded-lg">
                        <Heart className="w-4 h-4 text-red-400" />
                        <span className="text-sm font-medium">{likes.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-800/50 px-3 py-2 rounded-lg">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <span className="text-sm font-medium">Tendencia</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 md:gap-3">
                      <button
                        onClick={handleLike}
                        className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 px-4 md:px-6 py-2 md:py-3 rounded-xl transition-all shadow-lg"
                      >
                        <Heart className="w-4 h-4" />
                        <span className="font-semibold text-sm md:text-base">Me gusta</span>
                      </button>
                      <button className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-4 md:px-6 py-2 md:py-3 rounded-xl transition-all shadow-lg">
                        <Share2 className="w-4 h-4" />
                        <span className="font-semibold text-sm md:text-base">Compartir</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl p-8 md:p-12 text-center border border-gray-800">
                <div className="text-6xl md:text-8xl mb-6">📺</div>
                <h2 className="text-xl md:text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Selecciona un partido en vivo
                </h2>
                <p className="text-gray-400 text-base md:text-lg">
                  Disfruta de los mejores partidos en tiempo real con calidad HD
                </p>
              </div>
            )}
          </div>

          {/* Sidebar con Chat y Streams */}
          <div className="space-y-4 md:space-y-6">
            {/* Chat en vivo */}
            {currentStream && showChat && (
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl overflow-hidden border border-gray-800 shadow-xl">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-3 md:px-4 py-3">
                  <h3 className="font-bold flex items-center gap-2 text-sm md:text-base">
                    <MessageCircle className="w-4 h-4" />
                    Chat en Vivo
                  </h3>
                </div>
                
                <div 
                  ref={chatRef}
                  className="h-60 md:h-80 overflow-y-auto p-3 md:p-4 space-y-3 bg-gray-900/50"
                >
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">¡Sé el primero en comentar!</p>
                    </div>
                  ) : (
                    chatMessages.map((msg) => (
                      <div key={msg.id} className="flex items-start gap-2 md:gap-3">
                        <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          msg.isVIP 
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black' 
                            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                        }`}>
                          {msg.isVIP ? <Crown className="w-3 h-3 md:w-4 md:h-4" /> : msg.user.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs md:text-sm font-semibold ${
                              msg.isVIP ? 'text-yellow-400' : 'text-white'
                            }`}>
                              {msg.user}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-xs md:text-sm text-gray-300 bg-gray-800/50 p-2 rounded-lg">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="p-3 md:p-4 border-t border-gray-700 bg-gray-900">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Escribe un mensaje..."
                      className="flex-1 bg-gray-800 text-white px-3 md:px-4 py-2 md:py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-700 text-sm"
                    />
                    <button
                      onClick={sendMessage}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-4 md:px-6 py-2 md:py-3 rounded-xl transition-all font-semibold text-sm"
                    >
                      Enviar
                    </button>
                  </div>
                </div>
              </div>
            )}

                        {/* Streams disponibles */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl overflow-hidden border border-gray-800 shadow-xl">
              <div className={`px-3 md:px-4 py-3 ${
                viewMode === 'highlights' ? 'bg-gradient-to-r from-orange-600 to-red-600' :
                viewMode === 'live' ? 'bg-gradient-to-r from-red-600 to-pink-600' :
                'bg-gradient-to-r from-blue-600 to-purple-600'
              }`}>
                <h3 className="font-bold flex items-center gap-2 text-sm md:text-base">
                  {viewMode === 'highlights' ? <Play className="w-4 h-4" /> :
                   viewMode === 'live' ? <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div> :
                   <Clock className="w-4 h-4" />}
                  {viewMode === 'highlights' ? 'Resúmenes' :
                   viewMode === 'live' ? 'En Vivo' : 'Próximos'}
                </h3>
              </div>
              
              <div className="p-3 md:p-4 space-y-3 md:space-y-4 max-h-80 md:max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-gray-400 mt-2 text-sm">Cargando contenido...</p>
                  </div>
                ) : filteredStreams.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay contenido en esta categoría</p>
                  </div>
                ) : (
                  filteredStreams.map((stream) => (
                    <div 
                      key={stream.id}
                      className={`cursor-pointer rounded-lg md:rounded-xl overflow-hidden transition-all transform hover:scale-105 ${
                        currentStream?.id === stream.id 
                          ? 'ring-2 ring-blue-500 bg-blue-500/10 shadow-lg' 
                          : 'bg-gray-800/50 hover:bg-gray-700/50'
                      }`}
                      onClick={() => startStream(stream)}
                    >
                      <div className="relative">
                        <img 
                          src={stream.thumbnail} 
                          alt={stream.title}
                          className="w-full h-20 md:h-28 object-cover"
                        />
                        
                        {/* Badge de estado */}
                        <div className={`absolute top-1 md:top-2 left-1 md:left-2 text-white text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-bold ${
                          stream.status === 'live' ? 'bg-gradient-to-r from-red-600 to-red-700' :
                          stream.status === 'finished' ? 'bg-gradient-to-r from-green-600 to-green-700' :
                          'bg-gradient-to-r from-blue-600 to-blue-700'
                        }`}>
                          {stream.status === 'live' ? 'EN VIVO' :
                           stream.status === 'finished' ? 'RESUMEN' :
                           'PRÓXIMO'}
                        </div>
                        
                        {stream.isVIP && (
                          <div className="absolute top-1 md:top-2 right-1 md:right-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-bold flex items-center gap-1">
                            <Crown className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            VIP
                          </div>
                        )}
                        
                        <div className="absolute bottom-1 md:bottom-2 right-1 md:right-2 bg-black/70 text-white text-xs px-1 md:px-2 py-0.5 md:py-1 rounded">
                          {stream.quality}
                        </div>
                        
                        {/* Duración para resúmenes */}
                        {stream.status === 'finished' && stream.duration && (
                          <div className="absolute bottom-1 md:bottom-2 left-1 md:left-2 bg-black/70 text-white text-xs px-1 md:px-2 py-0.5 md:py-1 rounded">
                            {stream.duration}
                          </div>
                        )}
                      </div>
                      
                      <div className="p-2 md:p-3">
                        <h4 className="font-semibold text-xs md:text-sm mb-1 md:mb-2 line-clamp-2">{stream.title}</h4>
                        <div className="flex items-center justify-between text-xs text-gray-400 mb-1 md:mb-2">
                          <span className="font-medium">{stream.homeTeam} vs {stream.awayTeam}</span>
                        </div>
                        
                        {/* Highlights para resúmenes */}
                        {stream.status === 'finished' && stream.highlights && (
                          <div className="mb-2">
                            <div className="text-xs text-gray-400 mb-1">Highlights:</div>
                            <div className="flex flex-wrap gap-1">
                              {stream.highlights.slice(0, 2).map((highlight, index) => (
                                <span key={index} className="text-xs bg-gray-700/50 px-1 py-0.5 rounded text-gray-300">
                                  {highlight}
                                </span>
                              ))}
                              {stream.highlights.length > 2 && (
                                <span className="text-xs text-blue-400">+{stream.highlights.length - 2} más</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Eye className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            <span>{stream.viewers?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Heart className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            <span>{stream.likes?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Estadísticas rápidas */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl p-3 md:p-4 border border-gray-800">
              <h3 className="font-bold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
                <TrendingUp className="w-4 h-4" />
                Estadísticas
              </h3>
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-400">Partidos activos</span>
                  <span className="text-xs md:text-sm font-bold text-green-400">{filteredStreams.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-400">Espectadores totales</span>
                  <span className="text-xs md:text-sm font-bold text-blue-400">
                    {filteredStreams.reduce((sum, stream) => sum + (stream.viewers || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-400">Streams VIP</span>
                  <span className="text-xs md:text-sm font-bold text-yellow-400">
                    {filteredStreams.filter(stream => stream.isVIP).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal VIP */}
      {showVipModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl p-6 md:p-8 max-w-md mx-4 border border-gray-800">
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-6 h-6 md:w-8 md:h-8 text-black" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2">Contenido VIP</h3>
              <p className="text-gray-400 mb-6 text-sm md:text-base">
                Este partido es exclusivo para usuarios VIP. Actualiza tu cuenta para acceder a todos los partidos premium.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowVipModal(false)}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setShowVipModal(false);
                    navigate('/vip');
                  }}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 px-4 py-2 rounded-lg transition-colors font-semibold text-sm"
                >
                  Actualizar a VIP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promociones Banner */}
      {showPromotion && currentPromotion && (
        <div className="fixed top-4 right-4 z-40 max-w-sm">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-4 shadow-2xl border border-blue-400/30">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-yellow-300" />
                <h3 className="font-bold text-white">{currentPromotion.title}</h3>
              </div>
              <button
                onClick={() => setShowPromotion(false)}
                className="text-white/70 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-white/90 text-sm mb-3">{currentPromotion.description}</p>
            {currentPromotion.discount && (
              <div className="bg-yellow-500 text-black px-2 py-1 rounded text-xs font-bold mb-3 inline-block">
                {currentPromotion.discount}% DESCUENTO
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowPromotion(false);
                  navigate('/vip');
                }}
                className="flex-1 bg-white text-blue-600 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
              >
                Aprovechar
              </button>
              <button
                onClick={() => setShowPromotion(false)}
                className="px-3 py-2 text-white/70 hover:text-white text-sm"
              >
                Más tarde
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Anuncios Banner */}
      {!userData?.typeU === 'Cuenta Vip' && (
        <div className="fixed bottom-4 left-4 z-40 max-w-xs">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-3 shadow-2xl border border-red-400/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-300" />
                <span className="text-white font-semibold text-sm">¡SIN ANUNCIOS!</span>
              </div>
              <button
                onClick={() => setShowAd(false)}
                className="text-white/70 hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="text-white/90 text-xs mb-2">
              Actualiza a VIP y disfruta sin interrupciones
            </p>
            <button
              onClick={() => navigate('/vip')}
              className="w-full bg-white text-red-600 px-2 py-1 rounded text-xs font-bold hover:bg-gray-100 transition-colors"
            >
              Actualizar Ahora
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStreaming; 